import * as Sentry from "@sentry/react";
import { USER_STORAGE_PREFIX } from "./userStorageKey";

/**
 * Drops the signed-in employee's tab-scoped storage. Call it when signing out,
 * before handing over to `signoutRedirect`.
 *
 * Only sessionStorage needs clearing. localStorage is scoped per employee, so
 * the next person cannot read it in the first place, and clearing it would cost
 * every employee their site selection and preferences at each sign-out — which
 * on a shared computer is every shift. sessionStorage is the case scoping alone
 * does not cover: it belongs to the tab rather than to the session, so it
 * outlives a sign-out and a sign-in on that tab.
 *
 * Storage belonging to other tools is left alone, oidc-client-ts's among it:
 * `signoutRedirect` still needs the session to build its `id_token_hint`, and
 * removes the user itself once it has.
 */
export const clearUserSessionStorage = () => {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(USER_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    // SecurityError when site data is blocked. Signing out must still proceed.
    Sentry.captureException(error);
  }
};

/**
 * Drops the tab-scoped storage of every employee other than the one signing in.
 *
 * Signing out sweeps the tab on its way past, but that runs from the sign-out
 * button alone: an expired token goes straight to `signinRedirect`, so a shift
 * change that happens through expiry never reaches it. This is the same sweep
 * seen from the other end, and it also covers a sign-out whose own sweep never
 * finished — a closed lid, a killed browser, blocked site data — or one undone
 * by a write that landed while `signoutRedirect` was still awaiting.
 *
 * The owners are read off the keys, so nothing has to record who was here last.
 * Signing in is also the moment it stops mattering: until someone does, the
 * leftovers sit in a tab where every read is scoped to a signed-in employee.
 *
 * Runs during render rather than in an effect, so it happens before any hook
 * reads its key, and on every render of the gate rather than once: re-reading a
 * handful of keys costs nothing, and a key written after an earlier sweep is
 * then still caught.
 */
export const clearOtherUsersSessionStorage = (userId: string) => {
  const ownPrefix = `${USER_STORAGE_PREFIX}${userId}.`;
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(USER_STORAGE_PREFIX) && !key.startsWith(ownPrefix)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    // SecurityError when site data is blocked. The app works without storage.
    Sentry.captureException(error);
  }
};
